// src/pages/Campaigns/EventPost/EventPostAPIHelp.js

import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, IconButton, Tabs, Tab, CircularProgress,
  Alert, Card, CardContent, Divider, Chip, Stack
} from '@mui/material';
import {
  ArrowBack as BackIcon, Code as CodeIcon, ContentCopy as CopyIcon,
  CheckCircle as CheckIcon, Api as ApiIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../../utils/axiosInstance';

const EventPostAPIHelp = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();

  const [documentation, setDocumentation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [copiedItem, setCopiedItem] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  useEffect(() => {
    const loadDocumentation = async () => {
      try {
        const response = await axiosInstance.get('/api/event-posts/api-documentation', {
          params: { chatbot_id: chatbotId }
        });
        setDocumentation(response.data);
      } catch (error) {
        console.error('Error loading documentation:', error);
      } finally {
        setLoading(false);
      }
    };

    if (chatbotId) loadDocumentation();
  }, [chatbotId]);

  const handleCopy = async (text, itemId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItem(itemId);
      setTimeout(() => setCopiedItem(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const CodeBlock = ({ code, language, id }) => (
    <Box sx={{ position: 'relative', mt: 2 }}>
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 1
        }}
      >
        <IconButton
          size="small"
          onClick={() => handleCopy(code, id)}
          sx={{ bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
        >
          {copiedItem === id ? <CheckIcon fontSize="small" color="success" /> : <CopyIcon fontSize="small" />}
        </IconButton>
      </Box>
      <Box
        component="pre"
        sx={{
          bgcolor: '#1e1e1e',
          color: '#d4d4d4',
          p: 2,
          borderRadius: 1,
          overflow: 'auto',
          fontSize: '0.85rem',
          fontFamily: 'Monaco, Consolas, "Courier New", monospace',
          lineHeight: 1.5
        }}
      >
        <code>{code}</code>
      </Box>
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, mx: 'auto', mt:10 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/campaigns/event-posts`)}>
          <BackIcon />
        </IconButton>
        <Box>
          <Typography variant="h5" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ApiIcon /> API Integration Guide
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Integrate your event posts into external websites
          </Typography>
        </Box>
      </Box>

      {/* Info Card */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          The public API endpoints below allow you to fetch published posts from external websites without authentication.
          Use your Chatbot Identity: <strong>{documentation?.chatbot_identity}</strong>
        </Typography>
      </Alert>

      {/* API Endpoints */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
          Available Endpoints
        </Typography>

        {documentation?.endpoints?.map((endpoint, index) => (
          <Card key={index} variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Chip
                  label={endpoint.method}
                  color={endpoint.method === 'GET' ? 'success' : 'primary'}
                  size="small"
                />
                <Typography variant="subtitle1" fontWeight="bold">
                  {endpoint.name}
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {endpoint.description}
              </Typography>

              <Box
                sx={{
                  bgcolor: '#f5f5f5',
                  p: 1.5,
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '0.9rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <code>{endpoint.url}</code>
                <IconButton size="small" onClick={() => handleCopy(endpoint.url, `url-${index}`)}>
                  {copiedItem === `url-${index}` ? <CheckIcon fontSize="small" color="success" /> : <CopyIcon fontSize="small" />}
                </IconButton>
              </Box>

              {endpoint.parameters && endpoint.parameters.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Query Parameters:</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {endpoint.parameters.map((param, pIdx) => (
                      <Chip
                        key={pIdx}
                        label={`${param.name}=${param.default || '?'}`}
                        size="small"
                        variant="outlined"
                        title={param.description}
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              {endpoint.example_response && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2">Example Response:</Typography>
                  <CodeBlock
                    code={JSON.stringify(endpoint.example_response, null, 2)}
                    language="json"
                    id={`response-${index}`}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        ))}
      </Paper>

      {/* Code Examples */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
          Code Examples
        </Typography>

        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2 }}>
          <Tab label="JavaScript" />
          <Tab label="Python" />
          <Tab label="PHP" />
          <Tab label="cURL" />
        </Tabs>

        {activeTab === 0 && (
          <CodeBlock
            code={documentation?.code_examples?.javascript || '// Loading...'}
            language="javascript"
            id="code-js"
          />
        )}

        {activeTab === 1 && (
          <CodeBlock
            code={documentation?.code_examples?.python || '# Loading...'}
            language="python"
            id="code-python"
          />
        )}

        {activeTab === 2 && (
          <CodeBlock
            code={documentation?.code_examples?.php || '<?php // Loading...'}
            language="php"
            id="code-php"
          />
        )}

        {activeTab === 3 && (
          <CodeBlock
            code={documentation?.code_examples?.curl || '# Loading...'}
            language="bash"
            id="code-curl"
          />
        )}
      </Paper>

      {/* UCEF Integration Example */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
          PHP Integration Example (for UCEF Website)
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Complete PHP helper functions to integrate with your external website:
        </Typography>

        <CodeBlock
          code={`<?php
// events-api.php - Fetch events from KlonIT API

define('KLONIT_API_URL', '${documentation?.base_url || 'https://your-api.com'}/api/public/event-posts');
define('CHATBOT_ID', '${documentation?.chatbot_identity || 'your-chatbot-identity'}');

function fetchEventPosts($category = null, $page = 1, $perPage = 10) {
    $url = KLONIT_API_URL . '/' . CHATBOT_ID;

    if ($category) {
        $url .= '/category/' . urlencode($category);
    }

    $url .= '?page=' . $page . '&per_page=' . $perPage;

    $ch = curl_init();
    curl_setopt($ch, CURLOPT_URL, $url);
    curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
    curl_setopt($ch, CURLOPT_TIMEOUT, 30);

    $response = curl_exec($ch);
    $httpCode = curl_getinfo($ch, CURLINFO_HTTP_CODE);
    curl_close($ch);

    if ($httpCode === 200) {
        return json_decode($response, true);
    }

    return null;
}

function fetchSinglePost($slug) {
    $url = KLONIT_API_URL . '/' . CHATBOT_ID . '/slug/' . urlencode($slug);

    $ch = curl_init();
    curl_setopt($ch, CURLOPT_URL, $url);
    curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
    curl_setopt($ch, CURLOPT_TIMEOUT, 30);

    $response = curl_exec($ch);
    $httpCode = curl_getinfo($ch, CURLINFO_HTTP_CODE);
    curl_close($ch);

    if ($httpCode === 200) {
        $data = json_decode($response, true);
        return $data['data'] ?? null;
    }

    return null;
}

// Usage Example:
// $posts = fetchEventPosts(null, 1, 4);
// foreach ($posts['data'] as $post) {
//     echo $post['title'];
// }
?>`}
          language="php"
          id="code-ucef"
        />

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle2" sx={{ mb: 2 }}>HTML Template Example:</Typography>

        <CodeBlock
          code={`<!-- Display posts in your HTML -->
<?php
$eventsData = fetchEventPosts(null, 1, 4);

if ($eventsData && isset($eventsData['data'])) {
    foreach ($eventsData['data'] as $post) {
        $publishedDate = new DateTime($post['published_at']);
?>
<div class="event-card">
    <?php if ($post['featured_image_url']): ?>
        <img src="<?php echo htmlspecialchars($post['featured_image_url']); ?>"
             alt="<?php echo htmlspecialchars($post['title']); ?>" />
    <?php endif; ?>

    <h3><?php echo htmlspecialchars($post['title']); ?></h3>
    <p class="date"><?php echo $publishedDate->format('F j, Y'); ?></p>
    <p class="excerpt"><?php echo htmlspecialchars($post['excerpt']); ?></p>

    <a href="post.php?slug=<?php echo urlencode($post['slug']); ?>">
        Read More
    </a>
</div>
<?php
    }
}
?>`}
          language="php"
          id="code-html-template"
        />
      </Paper>
    </Box>
  );
};

export default EventPostAPIHelp;
